import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserProfile, getCurrentProgram, getCompletedActivityDays, getScheduledActivitiesForDate, getScheduledActivitiesForWeek, markActivityComplete, addSingleActivity, updateActivityDetails, deleteActivity, getMonthlyActivityMetrics, getCurrentStreak, getMonthlyStreakData, getActivityHeatData, saveWorkoutSession, checkProgramUpdateAvailable, resetProgramWorkouts, updateAllStrengthWorkoutTimes, getWorkoutSessionForActivity } from '../utils/database';
import { syncPendingWorkouts, getPendingCount } from '../utils/offlineQueue';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { getAppleStoreUrl, getGooglePlayUrl } from '../utils/storeLinks';
import '../styles/Dashboard.css';
import quotesData from '../data/quotes.json';
import PlaceholderIcon from '../assets/icons/placeholder.svg';
import CheckCircleIcon from '../assets/icons/check-circle.svg';
import EditIcon from '../assets/icons/edit-square.svg';
import ClockIcon from '../assets/icons/clock-stopwatch.svg';
import ChevronLeftDoubleIcon from '../assets/icons/chevron-left-double.svg';
import ChevronRightDoubleIcon from '../assets/icons/chevron-right-double.svg';
import Badge from '../assets/images/Begin-Badge.png';
import AppleIcon from '../assets/icons/apple.svg';
import GooglePlayIcon from '../assets/icons/google-play.svg';
import QuickAddActivityModal from '../components/QuickAddActivityModal';
import TimeEditModal from '../components/TimeEditModal';
import WorkoutFinishCard from '../components/WorkoutFinishCard';
import DashboardSidebar from '../components/DashboardSidebar';
import DashboardFooter from '../components/DashboardFooter';
import BottomNav from '../components/BottomNav';
import AppDownloadBanner from '../components/InstallPrompt';
import TeamSwitchConfirm from '../components/TeamSwitchConfirm';
import { useTheme } from '../contexts/ThemeContext';

// Helper to format date as YYYY-MM-DD in local time (avoids timezone issues)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to convert 24-hour time to 12-hour AM/PM format
const convertTo12Hour = (timeStr) => {
  if (!timeStr) return '';
  
  // If already in 12-hour format (contains AM/PM), return as is
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }
  
  // Try to parse 24-hour format (HH:mm or HH:mm:ss)
  const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!timeMatch) return timeStr;
  
  let hours = parseInt(timeMatch[1], 10);
  const minutes = timeMatch[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  
  if (hours === 0) {
    hours = 12;
  } else if (hours > 12) {
    hours = hours - 12;
  }
  
  return `${hours}:${minutes} ${period}`;
};

// Helper to get the week dates for the current week (normalized to midnight)
const getWeekDates = (weekOffset = 0) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to midnight
  const currentDayIdx = today.getDay();
  
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + (index - currentDayIdx) + weekOffset * 7);
    return date;
  });
};

/**
 * Get categorical activity name based on activityType and name
 * Returns simple category names: Run, Swim, Bike, Strength, etc.
 * Uses the program's activity names to determine category
 */
const getCategoricalActivityName = (activity) => {
  if (!activity) return 'Activity';
  
  const activityType = activity.activityType || activity.type;
  // Get name from multiple possible fields
  const name = (activity.name || activity.activityName || '').toLowerCase().trim();
  
  // Strength activities - use the specific workout name if available
  if (activityType === 'strength_workout' || activityType === 'strength' || 
      name.includes('strength session') || name.includes('strength training') || name.includes('strength')) {
    return activity.name && activity.name.toLowerCase() !== 'strength' ? activity.name : 'Strength';
  }
  
  // Recovery activities
  if (activityType === 'recovery' || name.includes('recovery')) {
    return 'Recovery';
  }
  
  // Sport-specific activities (volleyball, basketball, etc.)
  if (activityType === 'game' || name === 'game') {
    return 'Game';
  }
  if (activityType === 'practice' || name === 'practice' || name === 'practice / game') {
    return 'Practice';
  }
  if (activityType === 'drills' || name === 'drills') {
    return 'Drills';
  }
  if (name === 'cardio' || (activityType === 'endurance' && name === 'cardio')) {
    return 'Cardio';
  }
  
  // Swimming activities - check FIRST (most specific)
  if (name.includes('swim') || name.includes('pool swim') || name.includes('open water swim')) {
    return 'Swim';
  }
  
  // Biking activities - check for bike/ride keywords (check before run to catch "bike ride")
  // Must check "bike" before "ride" to avoid false matches
  if (name.includes('bike') || name.includes('mountain bike') || name.includes('road bike') || 
      name.includes('gravel bike') || name.includes('cycling') || 
      (name.includes('ride') && !name.includes('run'))) {
    return 'Bike';
  }
  
  // Walking activities - check for standalone "walk" (not "run/walk")
  if (name === 'walk' || (name.includes('walk') && !name.includes('run') && !name.includes('/'))) {
    return 'Walk';
  }
  
  // Running activities - includes "run/walk" combinations, "long run", etc.
  // This catches: "run", "long run", "run/walk", "trail run", "track workout"
  if (name.includes('run') || name.includes('track workout') || name.includes('cross country')) {
    return 'Run';
  }
  
  // Other specific activities
  if (name.includes('meditation')) {
    return 'Meditation';
  }
  if (name.includes('yoga')) {
    return 'Yoga';
  }
  if (name.includes('pilates')) {
    return 'Pilates';
  }
  if (name.includes('hiit')) {
    return 'HIIT';
  }
  if (name.includes('ski')) {
    return 'Ski';
  }
  
  // If we have an endurance activity but couldn't categorize, try to infer from description
  if (activityType === 'endurance' && name.length > 0) {
    const description = (activity.description || '').toLowerCase();
    
    // Check description for clues
    if (description.includes('swim') || description.includes('pool') || description.includes('water')) {
      return 'Swim';
    }
    if (description.includes('bike') || description.includes('cycling') || description.includes('ride')) {
      return 'Bike';
    }
    if (description.includes('run') || description.includes('running') || description.includes('track')) {
      return 'Run';
    }
    if (description.includes('walk')) {
      return 'Walk';
    }
    
    // If it's "Endurance Training" or similar generic name, infer from scheduled day
    // Based on program structure: Tue/Thu = Swim, Sun = Long Run/Bike, Mon/Wed/Fri = Run/Walk
    if (name.includes('endurance training') || (name.includes('endurance') && name.length < 20)) {
      const scheduledDate = activity.scheduledDate;
      if (scheduledDate) {
        try {
          const date = new Date(scheduledDate + 'T00:00:00');
          const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
          
          // Tuesday (2) and Thursday (4) are swim days in the program
          if (dayOfWeek === 2 || dayOfWeek === 4) {
            return 'Swim';
          }
          
          // Sunday (0) is long run or long bike - check month to infer
          if (dayOfWeek === 0) {
            const month = date.getMonth() + 1; // 1-12
            // Jan-Feb (winter): Long Run (bike off)
            // Mar-Oct: Long Bike Ride (bike on)
            // Nov-Dec: Long Run (bike off)
            if ((month >= 1 && month <= 2) || (month >= 11 && month <= 12)) {
              return 'Run';
            } else {
              return 'Bike';
            }
          }
          
          // Monday/Wednesday/Friday often have Run/Walk after strength
          if (dayOfWeek === 1 || dayOfWeek === 3 || dayOfWeek === 5) {
            return 'Run'; // Default to Run for weekdays
          }
        } catch (e) {
          // Ignore date parsing errors
        }
      }
    }
    
    console.warn('[getCategoricalActivityName] Could not categorize endurance activity:', { 
      name, 
      activityType, 
      activityName: activity.name,
      description: activity.description,
      scheduledDate: activity.scheduledDate,
      fullActivity: activity 
    });
  }
  
  // If name is a known activity label, use it directly (handles DB-defined activity names)
  if (name && name !== 'activity') {
    return activity.name || name.charAt(0).toUpperCase() + name.slice(1);
  }

  return 'Activity';
};

function Dashboard() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const justCompletedActivityId = location.state?.justCompletedActivityId;
  const [user, setUser] = useState(null);
  const [programData, setProgramData] = useState(null);
  const [completedDays, setCompletedDays] = useState([]);
  const [selectedDayActivities, setSelectedDayActivities] = useState([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState(new Date().getDay()); // 0 = Sunday
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = last week, +1 = next week
  // Per-day activity cache so switching to an already-viewed day is instant.
  // Data is still revalidated from Firestore in the background on every selection.
  const dayActivitiesCache = useRef(new Map());
  const latestDayRequestRef = useRef(null);
  const [, setIsLoading] = useState(true);
  const [activitiesLoaded, setActivitiesLoaded] = useState(false);
  const [metricsLoaded, setMetricsLoaded] = useState(false);
  const [isAddActivitiesModalOpen, setIsAddActivitiesModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null); // Activity being edited
  const [isTimeEditModalOpen, setIsTimeEditModalOpen] = useState(false);
  const [timeEditActivity, setTimeEditActivity] = useState(null); // Activity for time editing
  const [completedWorkoutSession, setCompletedWorkoutSession] = useState(null); // Session for "view completed" finish card
  const [monthlyDurationData, setMonthlyDurationData] = useState([]);
  const [monthlyDistanceData, setMonthlyDistanceData] = useState([]);
  const [monthlyStreakData, setMonthlyStreakData] = useState([]);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [activityHeatData, setActivityHeatData] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [userSettings, setUserSettings] = useState({
    distanceGoalType: 'weekly',
    distanceGoal: 20,           // miles per week
    durationGoalType: 'weekly',
    durationGoal: 150,          // minutes per week
    units: 'imperial',          // imperial or metric
  });
  
  // Program update state
  const [updateAvailable, setUpdateAvailable] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);

  // Team switch state (when arriving from a club/coach link while already onboarded)
  const pendingClubSlug = location.state?.pendingClubSlug || null;
  const pendingCoachSlug = location.state?.pendingCoachSlug || null;
  const [showTeamSwitch, setShowTeamSwitch] = useState(!!(pendingClubSlug || pendingCoachSlug));

  // Setup completion banner state (for club owners / coaches)
  const [setupBanner, setSetupBanner] = useState(null); // { name, link, type }
  const [setupBannerDismissed, setSetupBannerDismissed] = useState(false);

  // Desktop download banner (shown to non-owner users on desktop)
  const [showDesktopDownload, setShowDesktopDownload] = useState(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    const isMobile = /iphone|ipad|ipod|android/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isMobile) return false;
    const dismissed = localStorage.getItem('desktop-download-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) return false;
    return true;
  });
  
  // Helper to format distance with 1 decimal place + unit
  const formatDistance = (miles, showUnit = true) => {
    if (miles === 0 || miles === null || miles === undefined) return showUnit ? '0 mi' : '0';
    const formatted = miles.toFixed(1);
    return showUnit ? `${formatted} mi` : formatted;
  };
  
  // Helper to format duration as hours and minutes
  const formatDuration = (minutes) => {
    if (minutes === 0 || minutes === null || minutes === undefined) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };
  
  // Helper to calculate monthly goal from daily/weekly
  const getMonthlyDistanceGoal = (goalType, goalValue) => {
    if (goalType === 'daily') {
      return goalValue * 30; // ~30 days per month
    } else {
      return goalValue * 4.33; // ~4.33 weeks per month
    }
  };
  
  const getMonthlyDurationGoal = (goalType, goalValue) => {
    if (goalType === 'daily') {
      return goalValue * 30;
    } else {
      return goalValue * 4.33;
    }
  };
  
  // Calculate totals and averages from monthly data
  const getTotalDistance = () => {
    if (monthlyDistanceData.length === 0) return 0;
    return monthlyDistanceData.reduce((sum, d) => sum + (d.value || 0), 0);
  };
  
  const getTotalDuration = () => {
    if (monthlyDurationData.length === 0) return 0;
    return monthlyDurationData.reduce((sum, d) => sum + (d.value || 0), 0);
  };
  
  const getDailyAverageDistance = () => {
    const total = getTotalDistance();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    return total / daysInMonth;
  };
  
  const getDailyAverageDuration = () => {
    const total = getTotalDuration();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    return total / daysInMonth;
  };
  const [randomQuote] = useState(() => {
    const randomIndex = Math.floor(Math.random() * quotesData.length);
    return quotesData[randomIndex];
  });
  

  // Keep greeting visible on all screens

  useEffect(() => {
    // Use onAuthStateChanged to wait for Firebase to restore the session
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser || currentUser.uid !== userId) {
          navigate('/signin');
          return;
        }

        const userData = await getUserProfile(userId);
        if (!userData) {
          navigate('/onboarding');
          return;
        }

        setUser(userData);
        
        // Load user settings for goals (check monthlyGoals first, then settings for backwards compatibility)
        const goals = userData.monthlyGoals || userData.settings || {};
        setUserSettings({
          distanceGoalType: goals.distanceGoalType || 'weekly',
          distanceGoal: goals.distanceGoal || 20,           // 20 miles per week default
          durationGoalType: goals.durationGoalType || 'weekly',
          durationGoal: goals.durationGoal || 150,          // 150 minutes per week default
          units: userData.settings?.units || userData.preferences?.units || 'imperial',
        });
        
        // Load current program data
        const program = await getCurrentProgram(userId);
        if (program) {
          console.log('[Dashboard] Program data loaded:', {
            hasProgram: !!program.program,
            hasMacrocycle: !!program.macrocycle,
            hasMesocycle: !!program.mesocycle,
            macrocycle: program.macrocycle ? {
              name: program.macrocycle.name,
              startDate: program.macrocycle.startDate,
              endDate: program.macrocycle.endDate,
              hasProgress: !!program.macrocycle.progress
            } : null,
            mesocycle: program.mesocycle ? {
              name: program.mesocycle.name,
              startDate: program.mesocycle.startDate,
              endDate: program.mesocycle.endDate,
              hasProgress: !!program.mesocycle.progress
            } : null
          });
          setProgramData(program);
        } else {
          console.log('[Dashboard] No program data found for user');
        }
        
        // Load completed activity days for current month
        const now = new Date();
        const days = await getCompletedActivityDays(userId, now.getFullYear(), now.getMonth());
        setCompletedDays(days);
        
        // Load today's scheduled activities (initial load)
        const todayStr = formatLocalDate(now); // YYYY-MM-DD in local time
        const activities = await getScheduledActivitiesForDate(userId, todayStr);
        console.log('[Dashboard] Loaded activities for', todayStr, ':', activities.length, 'activities');
        console.log('[Dashboard] Activity sources:', activities.map(a => ({ name: a.name, source: a.source, scheduledDate: a.scheduledDate })));
        dayActivitiesCache.current.set(todayStr, activities);
        setSelectedDayActivities(activities);
        setSelectedDate(now);
        setSelectedDayIndex(now.getDay());
        setActivitiesLoaded(true); // Mark activities as loaded for progressive UI
        
        // Load activity metrics for last 30 days
        const endDate = new Date(now);
        const startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29); // 30 days including today
        
        const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const startDateStr = formatDate(startDate);
        const endDateStr = formatDate(endDate);
        
        console.log(`[Dashboard] Loading metrics for last 30 days: ${startDateStr} to ${endDateStr}`);
        
        const metrics = await getMonthlyActivityMetrics(userId, now.getFullYear(), now.getMonth(), startDateStr, endDateStr);
        setMonthlyDurationData(metrics.duration);
        setMonthlyDistanceData(metrics.distance); // Keep decimal precision for distance
        
        // Load streak data for last 30 days
        const streak = await getCurrentStreak(userId);
        setCurrentStreak(streak);
        const streakData = await getMonthlyStreakData(userId, now.getFullYear(), now.getMonth(), startDateStr, endDateStr);
        setMonthlyStreakData(streakData);
        
        // Load heat strip data for last 30 days
        const heatData = await getActivityHeatData(userId, 30);
        setActivityHeatData(heatData);
        setMetricsLoaded(true); // Mark metrics as loaded for progressive UI
        
        // Sync any pending offline workouts
        try {
          const pendingCount = await getPendingCount();
          if (pendingCount > 0) {
            console.log('[Dashboard] Found', pendingCount, 'pending workouts to sync');
            const result = await syncPendingWorkouts(userId, saveWorkoutSession);
            if (result.synced > 0) {
              console.log('[Dashboard] Synced', result.synced, 'pending workouts');
              // Refresh completed days after sync
              const refreshedDays = await getCompletedActivityDays(userId, now.getFullYear(), now.getMonth());
              setCompletedDays(refreshedDays);
            }
          }
        } catch (syncError) {
          console.warn('[Dashboard] Error syncing pending workouts:', syncError);
          // Don't block dashboard load for sync errors
        }
        
        // Check for program updates
        try {
          const updateCheck = await checkProgramUpdateAvailable(userId);
          if (updateCheck.needsUpdate) {
            console.log('[Dashboard] Program update available:', updateCheck);
            setUpdateAvailable(updateCheck);
          }
        } catch (updateError) {
          console.warn('[Dashboard] Error checking for updates:', updateError);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('[Dashboard] Error loading user:', error);
        navigate('/signin');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [userId, navigate, location.state?.refreshed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load completed days when calendar month changes
  useEffect(() => {
    const loadCompletedDays = async () => {
      if (!userId) return;
      try {
        const days = await getCompletedActivityDays(userId, calendarDate.getFullYear(), calendarDate.getMonth());
        setCompletedDays(days);
      } catch (error) {
        console.error('[Dashboard] Error loading completed days:', error);
      }
    };
    loadCompletedDays();
  }, [userId, calendarDate]);

  // Check setup completion for club owners / coaches
  useEffect(() => {
    if (!user) return;
    const isClubOwner = user.role === 'club_owner' || user.ownedClubIds?.length > 0;
    const dismissKey = `setup-banner-dismissed-${isClubOwner ? 'club' : 'coach'}`;
    const dismissedAt = localStorage.getItem(dismissKey);
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < 7 * 24 * 60 * 60 * 1000) {
      return; // dismissed within last 7 days
    }
    (async () => {
      try {
        if (user.role === 'club_owner' || user.ownedClubIds?.length) {
          if (user.ownedClubIds?.length) {
            const { getClub } = await import('../utils/clubs');
            const club = await getClub(user.ownedClubIds[0]);
            if (club) {
              const teamsConfigured = club.teams?.length > 0;
              const pricingSet = club.markupPerMonth != null;
              if (!teamsConfigured || !pricingSet) {
                setSetupBanner({ name: club.name, link: `/${userId}/club/${club.id}/manage`, type: 'club' });
              }
            }
          } else {
            setSetupBanner({ name: 'your club', link: '/clubs/signup', type: 'club' });
          }
        } else if (user.role === 'coach') {
          if (user.coachId) {
            const { getCoach } = await import('../utils/coaches');
            const coach = await getCoach(user.coachId);
            if (coach) {
              setSetupBanner({ name: coach.name, link: `/${userId}/coach/${coach.id}`, type: 'coach' });
            }
          } else {
            setSetupBanner({ name: 'your team', link: '/coach/signup', type: 'coach' });
          }
        }
      } catch (err) {
        console.error('[Dashboard] Setup banner check error:', err);
      }
    })();
  }, [user, userId]);

  const handleDismissSetupBanner = () => {
    const dismissKey = `setup-banner-dismissed-${setupBanner?.type || 'unknown'}`;
    localStorage.setItem(dismissKey, String(Date.now()));
    setSetupBannerDismissed(true);
  };

  // Don't show loading screen - render skeleton immediately
  const firstName = (user?.firstName || user?.displayName?.split(' ')[0] || 'User').trim();
  
  // Memoize day arrays to prevent unnecessary re-renders
  const days = useMemo(() => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], []);
  const daysShort = useMemo(() => ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'], []);
  
  // Memoize week dates to avoid recalculating on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekDates = useMemo(() => getWeekDates(weekOffset), [weekOffset]);

  // Load (and cache) activities for a specific date, with instant cache render
  // and background revalidation. Shared by day taps and week navigation.
  const loadDayActivities = useCallback(async (targetDate) => {
    const dateStr = formatLocalDate(targetDate);
    setSelectedDate(targetDate);
    latestDayRequestRef.current = dateStr;

    // Instant render from cache if we've already loaded this day.
    const cached = dayActivitiesCache.current.get(dateStr);
    if (cached) {
      setSelectedDayActivities(cached);
    }

    // Always revalidate in the background so completion/edits stay fresh.
    try {
      const activities = await getScheduledActivitiesForDate(userId, dateStr);
      dayActivitiesCache.current.set(dateStr, activities);
      // Guard against out-of-order responses when tapping/navigating quickly.
      if (latestDayRequestRef.current === dateStr) {
        console.log('[Dashboard] Loaded activities for', dateStr, ':', activities.length, 'activities');
        setSelectedDayActivities(activities);
      }
    } catch (error) {
      console.warn('[Dashboard] Error loading day activities:', error);
    }
  }, [userId]);

  // Handler for day selection - uses weekDates from memo
  const handleDaySelect = useCallback((index) => {
    setSelectedDayIndex(index);
    return loadDayActivities(weekDates[index]);
  }, [weekDates, loadDayActivities]);

  // Move to the previous / next week, keeping the same weekday selected.
  const changeWeek = useCallback((delta) => {
    const newOffset = weekOffset + delta;
    setWeekOffset(newOffset);
    const newWeek = getWeekDates(newOffset);
    return loadDayActivities(newWeek[selectedDayIndex]);
  }, [weekOffset, selectedDayIndex, loadDayActivities]);

  // Background-prefetch the whole visible week into the cache in a SINGLE ranged
  // query once the critical dashboard content has loaded, so day-to-day
  // navigation is instant. Runs after initial render, so it never blocks paint.
  useEffect(() => {
    if (!userId || !activitiesLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const dateStrs = weekDates.map((d) => formatLocalDate(d));
        const byDate = await getScheduledActivitiesForWeek(userId, dateStrs);
        if (cancelled) return;
        Object.entries(byDate).forEach(([ds, acts]) => {
          dayActivitiesCache.current.set(ds, acts);
        });
        // Refresh the currently-selected day from the freshly cached week.
        const selectedStr = formatLocalDate(selectedDate);
        if (byDate[selectedStr] && latestDayRequestRef.current === null) {
          setSelectedDayActivities(byDate[selectedStr]);
        }
      } catch (error) {
        console.warn('[Dashboard] Week prefetch failed:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, activitiesLoaded, weekDates]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Macrocycle progress calculation (time-based)
  const calculateTimeProgress = (startDate, endDate) => {
    if (!startDate || !endDate) {
      console.log('[Dashboard] calculateTimeProgress: Missing dates', { startDate, endDate });
      return 0;
    }
    
    let start, end;
    
    try {
      console.log('[Dashboard] calculateTimeProgress INPUT:', { 
        startDate, 
        endDate, 
        startDateType: typeof startDate, 
        endDateType: typeof endDate,
        startDateHasToDate: typeof startDate?.toDate === 'function',
        startDateHasSeconds: startDate?.seconds !== undefined,
        endDateHasToDate: typeof endDate?.toDate === 'function',
        endDateHasSeconds: endDate?.seconds !== undefined
      });
      
      // Handle Firestore Timestamp for startDate
      // Check if it's a Firestore Timestamp by checking for seconds property or toDate method
      if (startDate && typeof startDate === 'object') {
        if (typeof startDate.toDate === 'function') {
          start = startDate.toDate();
          console.log('[Dashboard] Used toDate() for startDate:', start.toISOString());
        } else if (startDate.seconds !== undefined) {
          // Firestore Timestamp has seconds property
          start = new Date(startDate.seconds * 1000 + (startDate.nanoseconds || 0) / 1000000);
          console.log('[Dashboard] Used seconds for startDate:', start.toISOString(), 'from seconds:', startDate.seconds);
        } else if (startDate instanceof Date) {
          start = startDate;
        } else {
          start = new Date(startDate);
        }
      } else if (typeof startDate === 'string') {
        // Handle string dates (YYYY-MM-DD format)
        start = new Date(startDate + 'T00:00:00'); // Add time to avoid timezone issues
      } else {
        start = new Date(startDate);
      }
      
      // Handle Firestore Timestamp for endDate
      if (endDate && typeof endDate === 'object') {
        if (typeof endDate.toDate === 'function') {
          end = endDate.toDate();
          console.log('[Dashboard] Used toDate() for endDate:', end.toISOString());
        } else if (endDate.seconds !== undefined) {
          // Firestore Timestamp has seconds property
          end = new Date(endDate.seconds * 1000 + (endDate.nanoseconds || 0) / 1000000);
          console.log('[Dashboard] Used seconds for endDate:', end.toISOString(), 'from seconds:', endDate.seconds);
        } else if (endDate instanceof Date) {
          end = endDate;
        } else {
          end = new Date(endDate);
        }
      } else if (typeof endDate === 'string') {
        // Handle string dates (YYYY-MM-DD format)
        end = new Date(endDate + 'T00:00:00'); // Add time to avoid timezone issues
      } else {
        end = new Date(endDate);
      }
      
      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.log('[Dashboard] calculateTimeProgress: Invalid dates', { start, end, startDate, endDate, startType: typeof startDate, endType: typeof endDate });
        return 0;
      }
      
      const now = new Date();
      const total = end - start;
      const elapsed = now - start;
      
      if (total <= 0) {
        console.log('[Dashboard] calculateTimeProgress: Invalid date range', { start: start.toISOString(), end: end.toISOString(), total });
        return 0;
      }
      
      const progress = Math.min(100, Math.max(0, (elapsed / total) * 100));
      console.log('[Dashboard] calculateTimeProgress result:', { progress, start: start.toISOString(), end: end.toISOString(), now: now.toISOString(), total: total / (1000 * 60 * 60 * 24) + ' days', elapsed: elapsed / (1000 * 60 * 60 * 24) + ' days' });
      return progress;
    } catch (error) {
      console.error('[Dashboard] calculateTimeProgress error:', error, { startDate, endDate });
      return 0;
    }
  };
  
  const calculateTimeRemaining = (endDate) => {
    if (!endDate) {
      console.log('[Dashboard] calculateTimeRemaining: No endDate provided');
      return 'N/A';
    }
    
    try {
      let end;
      // Handle Firestore Timestamp
      if (endDate && typeof endDate === 'object') {
        if (typeof endDate.toDate === 'function') {
          end = endDate.toDate();
        } else if (endDate.seconds !== undefined) {
          // Firestore Timestamp has seconds property
          end = new Date(endDate.seconds * 1000 + (endDate.nanoseconds || 0) / 1000000);
        } else if (endDate instanceof Date) {
          end = endDate;
        } else {
          end = new Date(endDate);
        }
      } else if (typeof endDate === 'string') {
        end = new Date(endDate + 'T00:00:00'); // Add time to avoid timezone issues
      } else {
        end = new Date(endDate);
      }
      
      if (isNaN(end.getTime())) {
        console.log('[Dashboard] calculateTimeRemaining: Invalid date', { endDate, end, endDateType: typeof endDate });
        return 'N/A';
      }
      
      const now = new Date();
      const diffMs = end - now;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) return 'Complete';
      if (diffDays < 7) return `${diffDays} days`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks`;
      return `${Math.ceil(diffDays / 30)} months`;
    } catch (error) {
      console.error('[Dashboard] calculateTimeRemaining error:', error, { endDate });
      return 'N/A';
    }
  };
  
  // Use stored progress if available, otherwise calculate from dates
  const macrocycleData = useMemo(() => {
    let progress = 0;
    let timeRemaining = 'N/A';
    let name = 'No Active Macrocycle';
    
    if (programData?.macrocycle) {
      name = programData.macrocycle.name || 'No Active Macrocycle';
      console.log('[Dashboard] Macrocycle data:', {
        name: name,
        startDate: programData.macrocycle.startDate ? String(programData.macrocycle.startDate) : 'MISSING',
        endDate: programData.macrocycle.endDate ? String(programData.macrocycle.endDate) : 'MISSING',
        startDateType: typeof programData.macrocycle.startDate,
        endDateType: typeof programData.macrocycle.endDate,
        hasProgress: !!programData.macrocycle.progress,
        progressTimeProgress: programData.macrocycle.progress?.timeProgress,
        progressTimeRemaining: programData.macrocycle.progress?.timeRemaining,
        hasMesocycle: !!programData.mesocycle,
        mesocycleStartDate: programData.mesocycle?.startDate ? String(programData.mesocycle.startDate) : 'NONE',
        mesocycleEndDate: programData.mesocycle?.endDate ? String(programData.mesocycle.endDate) : 'NONE'
      });
      
      // Always calculate from dates if available
      if (programData.macrocycle.startDate && programData.macrocycle.endDate) {
        console.log('[Dashboard] Using macrocycle dates for progress');
        progress = calculateTimeProgress(programData.macrocycle.startDate, programData.macrocycle.endDate);
        timeRemaining = calculateTimeRemaining(programData.macrocycle.endDate);
      } else if (programData.mesocycle && programData.mesocycle.startDate && programData.mesocycle.endDate) {
        // Fallback: Use mesocycle dates if macrocycle dates are missing
        console.log('[Dashboard] Using mesocycle dates as fallback for macrocycle progress', {
          mesocycleStartDate: programData.mesocycle.startDate,
          mesocycleEndDate: programData.mesocycle.endDate,
          mesocycleStartDateType: typeof programData.mesocycle.startDate,
          mesocycleEndDateType: typeof programData.mesocycle.endDate
        });
        progress = calculateTimeProgress(programData.mesocycle.startDate, programData.mesocycle.endDate);
        const calculatedRemaining = calculateTimeRemaining(programData.mesocycle.endDate);
        console.log('[Dashboard] Calculated macrocycle time remaining from mesocycle:', calculatedRemaining);
        timeRemaining = calculatedRemaining;
      } else if (programData.macrocycle.progress?.timeProgress !== undefined && programData.macrocycle.progress.timeProgress > 0) {
        // Only use stored progress if dates aren't available and stored value is > 0
        console.log('[Dashboard] Using stored macrocycle progress');
        progress = programData.macrocycle.progress.timeProgress;
        timeRemaining = programData.macrocycle.progress.timeRemaining || 'N/A';
      } else {
        console.log('[Dashboard] No dates or stored progress available for macrocycle');
      }
      console.log('[Dashboard] Macrocycle progress calculated:', {
        name: name,
        progress: progress,
        timeRemaining: timeRemaining,
        hasStoredProgress: !!programData.macrocycle.progress?.timeProgress,
        hasDates: !!(programData.macrocycle.startDate && programData.macrocycle.endDate)
      });
    } else {
      console.log('[Dashboard] No macrocycle data in programData:', programData);
    }
    
    return { progress, timeRemaining, name };
  }, [programData]);
  
  const macrocycleTimeRemaining = macrocycleData.timeRemaining;
  const macrocycleName = macrocycleData.name;
  
  // Calculate daily progress for macrocycle
  const macrocycleDays = useMemo(() => {
    if (!programData?.macrocycle && !programData?.mesocycle) {
      return [];
    }
    
    // Use macrocycle dates if available, otherwise use mesocycle dates
    let startDate, endDate;
    if (programData?.macrocycle?.startDate && programData?.macrocycle?.endDate) {
      startDate = programData.macrocycle.startDate;
      endDate = programData.macrocycle.endDate;
    } else if (programData?.mesocycle?.startDate && programData?.mesocycle?.endDate) {
      startDate = programData.mesocycle.startDate;
      endDate = programData.mesocycle.endDate;
    } else {
      return [];
    }
    
    // Convert to Date objects
    let start, end;
    try {
      if (startDate && typeof startDate === 'object') {
        if (typeof startDate.toDate === 'function') {
          start = startDate.toDate();
        } else if (startDate.seconds !== undefined) {
          start = new Date(startDate.seconds * 1000 + (startDate.nanoseconds || 0) / 1000000);
        } else {
          start = new Date(startDate);
        }
      } else if (typeof startDate === 'string') {
        start = new Date(startDate + 'T00:00:00');
      } else {
        start = new Date(startDate);
      }
      
      if (endDate && typeof endDate === 'object') {
        if (typeof endDate.toDate === 'function') {
          end = endDate.toDate();
        } else if (endDate.seconds !== undefined) {
          end = new Date(endDate.seconds * 1000 + (endDate.nanoseconds || 0) / 1000000);
        } else {
          end = new Date(endDate);
        }
      } else if (typeof endDate === 'string') {
        end = new Date(endDate + 'T00:00:00');
      } else {
        end = new Date(endDate);
      }
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return [];
      }
    } catch (error) {
      console.error('[Dashboard] Error parsing dates for days:', error);
      return [];
    }
    
    // Calculate all days
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const days = [];
    const currentDate = new Date(start);
    currentDate.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(end);
    endDateOnly.setHours(0, 0, 0, 0);
    
    let dayIndex = 0;
    while (currentDate <= endDateOnly) {
      const dayOfWeek = currentDate.getDay();
      const isWeekStart = dayOfWeek === 0 && dayIndex > 0; // Sunday is start of week, but not the first day
      
      let status = 'empty'; // 'empty', 'half', 'full'
      
      if (currentDate < now) {
        // Day is in the past - completed
        status = 'full';
      } else if (currentDate.getTime() === now.getTime()) {
        // Today - in progress (half)
        status = 'half';
      } else {
        // Future day - not started
        status = 'empty';
      }
      
      days.push({
        date: new Date(currentDate),
        status,
        dayIndex,
        isWeekStart
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
      dayIndex++;
    }
    
    return days;
  }, [programData]);
  
  // Use stored progress if available, otherwise calculate from dates
  let mesocycleProgress = 0;
  let mesocycleWeeks = 6;
  let mesocycleCompletionProgress = 0;
  
  if (programData?.mesocycle) {
    mesocycleWeeks = programData.mesocycle.totalWeeks || 6;
    console.log('[Dashboard] Mesocycle data:', {
      name: programData.mesocycle.name,
      startDate: programData.mesocycle.startDate ? String(programData.mesocycle.startDate) : 'MISSING',
      endDate: programData.mesocycle.endDate ? String(programData.mesocycle.endDate) : 'MISSING',
      startDateType: typeof programData.mesocycle.startDate,
      endDateType: typeof programData.mesocycle.endDate,
      hasProgress: !!programData.mesocycle.progress,
      progressTimeProgress: programData.mesocycle.progress?.timeProgress,
      progressCompletionProgress: programData.mesocycle.progress?.completionProgress,
      totalWeeks: mesocycleWeeks
    });
    
    // Always calculate from dates if available, since stored progress might be 0 (which is falsy but valid)
    if (programData.mesocycle.startDate && programData.mesocycle.endDate) {
      console.log('[Dashboard] Calling calculateTimeProgress for mesocycle with:', {
        startDate: programData.mesocycle.startDate,
        endDate: programData.mesocycle.endDate,
        startDateType: typeof programData.mesocycle.startDate,
        endDateType: typeof programData.mesocycle.endDate
      });
      mesocycleProgress = calculateTimeProgress(programData.mesocycle.startDate, programData.mesocycle.endDate);
      console.log('[Dashboard] calculateTimeProgress returned:', mesocycleProgress);
    } else if (programData.mesocycle.progress?.timeProgress !== undefined && programData.mesocycle.progress.timeProgress > 0) {
      // Only use stored progress if dates aren't available and stored value is > 0
      mesocycleProgress = programData.mesocycle.progress.timeProgress;
      console.log('[Dashboard] Using stored mesocycle progress:', mesocycleProgress);
    } else {
      console.log('[Dashboard] Mesocycle dates missing - cannot calculate progress', {
        hasStartDate: !!programData.mesocycle.startDate,
        hasEndDate: !!programData.mesocycle.endDate,
        hasStoredProgress: programData.mesocycle.progress?.timeProgress !== undefined
      });
    }
    mesocycleCompletionProgress = programData.mesocycle.progress?.completionProgress || 0;
    console.log('[Dashboard] Mesocycle progress calculated:', {
      name: programData.mesocycle.name,
      progress: mesocycleProgress,
      progressRounded: Math.round(mesocycleProgress),
      progressForBar: `${Math.max(0, Math.min(100, mesocycleProgress))}%`,
      completionProgress: mesocycleCompletionProgress,
      weeks: mesocycleWeeks,
      hasStoredProgress: !!programData.mesocycle.progress?.timeProgress,
      hasDates: !!(programData.mesocycle.startDate && programData.mesocycle.endDate)
    });
  } else {
    console.log('[Dashboard] No mesocycle data in programData:', programData);
  }

  // Generate calendar days for displayed month
  const calendarMonth = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const firstDayOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1).getDay();
  const daysInMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0).getDate();
  const calendarDays = [];
  
  // Empty cells for days before month starts
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  
  // Days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  // Navigate to previous/next month
  const goToPreviousMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  // Handle program update
  const handleProgramUpdate = async () => {
    if (!updateAvailable || isUpdating) return;
    
    setIsUpdating(true);
    setUpdateMessage(null);
    
    try {
      console.log('[Dashboard] Starting program update...');
      const result = await resetProgramWorkouts(userId, updateAvailable.newVersion);
      
      if (result.success) {
        setUpdateMessage({
          type: 'success',
          text: `Updated! Refreshed ${result.createdCount} workouts.`
        });
        setUpdateAvailable(null);
        
        // Refresh activities for selected day
        const dateStr = formatLocalDate(selectedDate);
        const activities = await getScheduledActivitiesForDate(userId, dateStr);
        setSelectedDayActivities(activities);
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setUpdateMessage(null);
        }, 3000);
      }
    } catch (error) {
      console.error('[Dashboard] Error updating program:', error);
      setUpdateMessage({
        type: 'error',
        text: 'Update failed. Please try again.'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
    {showTeamSwitch && (
      <TeamSwitchConfirm
        userId={userId}
        pendingClubSlug={pendingClubSlug}
        pendingCoachSlug={pendingCoachSlug}
        onComplete={() => {
          setShowTeamSwitch(false);
          window.location.reload();
        }}
        onDismiss={() => setShowTeamSwitch(false)}
      />
    )}
    <div className="dashboard-page has-bottom-nav">
      {/* Background effects */}
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />

      {/* Sidebar */}
      <DashboardSidebar userId={userId} user={user} />

      {/* Main content */}
      <div className="dashboard-main">
        {/* Header */}
        <div className="dashboard-header">
          {/* Mobile profile picture - visible only on PWA/mobile */}
          <div className="dashboard-mobile-profile">
            <img 
              src={user?.profilePictureUrl || Badge} 
              alt="Profile" 
              className="dashboard-mobile-profile-pic"
            />
          </div>
          <div className="dashboard-greeting">
            {user ? (
              <>
                <h1 className="dashboard-content-loaded">Hello, <span className="dashboard-name">{firstName}!</span></h1>
                <p className="dashboard-quote dashboard-content-loaded">{randomQuote.quote} — {randomQuote.author}</p>
              </>
            ) : (
              <div className="dashboard-skeleton-header">
                <div className="dashboard-skeleton-title" />
                <div className="dashboard-skeleton-subtitle" />
              </div>
            )}
          </div>
        </div>

        {/* Program Update Banner */}
        {updateAvailable && (
          <div className="dashboard-update-banner">
            <div className="dashboard-update-content">
              <div className="dashboard-update-text">
                <strong>Program Update Available</strong>
                <p>New workout templates are available for {updateAvailable.programName}. Your completed workouts will be preserved.</p>
              </div>
              <button 
                className="c-ripple dashboard-update-button"
                onClick={handleProgramUpdate}
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        )}

        {/* Update Message (Success/Error) */}
        {updateMessage && (
          <div className={`dashboard-update-message ${updateMessage.type}`}>
            {updateMessage.text}
          </div>
        )}

        {/* Setup Completion Banner (club owners / coaches) */}
        {setupBanner && !setupBannerDismissed && (
          <div className="dashboard-setup-banner">
            <div className="dashboard-setup-banner-content">
              <div className="dashboard-setup-banner-text">
                <strong>Finish setting up {setupBanner.name}</strong>
                <p>Complete your {setupBanner.type === 'club' ? 'club' : 'team'} setup so athletes can find you.</p>
              </div>
              <div className="dashboard-setup-banner-actions">
                <button
                  className="c-ripple dashboard-setup-banner-btn"
                  onClick={() => navigate(setupBanner.link)}
                >
                  Complete Setup
                </button>
                <button
                  className="dashboard-setup-banner-dismiss"
                  onClick={handleDismissSetupBanner}
                  aria-label="Dismiss"
                >
                  &times;
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Download Banner */}
        {showDesktopDownload && (
          <div className="dashboard-setup-banner" style={{ borderColor: 'rgba(162, 150, 255, 0.25)', background: 'linear-gradient(135deg, rgba(162, 150, 255, 0.12) 0%, rgba(120, 100, 220, 0.06) 100%)' }}>
            <div className="dashboard-setup-banner-content">
              <div className="dashboard-setup-banner-text">
                <strong style={{ color: '#A296FF' }}>Download the App</strong>
                <p>Get the full experience on your phone — track workouts, follow your program, and more.</p>
              </div>
              <div className="dashboard-setup-banner-actions">
                <a
                  href={getAppleStoreUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-button-apple"
                  style={{ margin: 0 }}
                >
                  <img src={AppleIcon} alt="Apple" />
                  <div className="download-button-text">
                    <span className="download-button-text-top">Download on the</span>
                    <span className="download-button-text-bottom">App Store</span>
                  </div>
                </a>
                <a
                  href={getGooglePlayUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-button"
                  style={{ margin: 0 }}
                >
                  <img src={GooglePlayIcon} alt="Google Play" />
                  <div className="download-button-text">
                    <span className="download-button-text-top">GET IT ON</span>
                    <span className="download-button-text-bottom">Google <strong>Play</strong></span>
                  </div>
                </a>
                <button
                  className="dashboard-setup-banner-dismiss"
                  onClick={() => {
                    setShowDesktopDownload(false);
                    localStorage.setItem('desktop-download-dismissed', String(Date.now()));
                  }}
                  aria-label="Dismiss"
                >
                  &times;
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Grid */}
        <div className="dashboard-content-grid">
          {/* Microcycle Activity - Left Side */}
          <div className="dashboard-microcycle-container">
            <div className="dashboard-card dashboard-microcycle-card">
              <div className="dashboard-card-header">
                <h2>Microcycle Activity</h2>
                <p className="dashboard-activity-count">
                  {(() => {
                    const today = new Date();
                    const isToday = selectedDate.getDate() === today.getDate() &&
                                    selectedDate.getMonth() === today.getMonth() &&
                                    selectedDate.getFullYear() === today.getFullYear();
                    const dayLabel = isToday ? 'Today' : days[selectedDayIndex];
                    
                    return selectedDayActivities.length > 0 
                      ? `${selectedDayActivities.length} ${selectedDayActivities.length === 1 ? 'activity' : 'activities'} ${isToday ? '' : 'on '}${dayLabel}`
                      : `No activities ${isToday ? '' : 'on '}${dayLabel}`;
                  })()}
                </p>
              </div>
              
              {/* Week navigation + day selector */}
              <div className="dashboard-week-nav">
                <button
                  type="button"
                  className="c-ripple dashboard-week-nav-arrow"
                  onClick={() => changeWeek(-1)}
                  aria-label="Previous week"
                  title="Previous week"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <div className="dashboard-day-selector">
                  {days.map((day, index) => (
                    <div 
                      key={day}
                      className={`c-ripple dashboard-day-button ${index === selectedDayIndex ? 'active' : ''}`}
                      onClick={() => handleDaySelect(index)}
                      title={`${day} - ${formatLocalDate(weekDates[index])}`}
                    >
                      <span className="dashboard-day-button-full">{day}</span>
                      <span className="dashboard-day-button-short">{daysShort[index]}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="c-ripple dashboard-week-nav-arrow"
                  onClick={() => changeWeek(1)}
                  aria-label="Next week"
                  title="Next week"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              {/* Activity cards */}
              <div className="dashboard-activities">
                {/* Skeleton loading state */}
                {!activitiesLoaded && (
                  <>
                    <div className="dashboard-skeleton-activity" />
                    <div className="dashboard-skeleton-activity" />
                    <div className="dashboard-skeleton-activity" />
                  </>
                )}
                
                {/* Loaded activities */}
                {activitiesLoaded && selectedDayActivities.length > 0 && (
                  selectedDayActivities.map((activity) => {
                    // Activities from program are orange, user-added are grey
                    const isFromProgram = activity.source === 'program';
                    const isCompleted = activity.status === 'completed';
                    const statusClass = isCompleted ? 'completed' : '';
                    // Check for strength workouts - handle different field names
                    const isStrengthWorkout = activity.activityType === 'strength_workout' || activity.activityType === 'strength';
                    
                    const handleMarkComplete = async (e) => {
                      e.stopPropagation();
                      if (!activity.activityId) {
                        console.error('[Dashboard] Cannot mark complete: activityId is missing', activity);
                        return;
                      }
                      const dateStr = formatLocalDate(selectedDate);
                      // Toggle: if completed, mark as incomplete; if not completed, mark as complete
                      await markActivityComplete(userId, activity.activityId, dateStr, !isCompleted);
                      
                      // Refresh activities for selected day
                      const activities = await getScheduledActivitiesForDate(userId, dateStr);
                      setSelectedDayActivities(activities);
                      
                      // Refresh completed days for calendar
                      const now = new Date();
                      const completedDaysList = await getCompletedActivityDays(userId, now.getFullYear(), now.getMonth());
                      setCompletedDays(completedDaysList);
                      
                      // Refresh performance metrics (duration/distance)
                      const metricsData = await getMonthlyActivityMetrics(userId, now.getFullYear(), now.getMonth());
                      if (metricsData) {
                        setMonthlyDurationData(metricsData.duration || []);
                        setMonthlyDistanceData(metricsData.distance || []);
                      }
                    };
                    
                    const handleActivityClick = async () => {
                      if (!isStrengthWorkout) return;
                      if (isCompleted) {
                        // Show completed workout finish card (same as Workout page save)
                        const session = await getWorkoutSessionForActivity(userId, activity.activityId, formatLocalDate(selectedDate));
                        if (session) setCompletedWorkoutSession(session);
                        return;
                      }
                      // Navigate to workout page for incomplete strength workouts
                      const templateId = activity.workoutTemplateId || activity.templateId;
                      navigate(`/${userId}/workout`, {
                        state: {
                          workout: {
                            activityId: activity.activityId,
                            workoutTemplateId: templateId,
                            name: activity.name,
                            description: activity.description,
                            scheduledDate: formatLocalDate(selectedDate),
                            estimatedDuration: activity.estimatedDuration || 60,
                            status: activity.status,
                            programId: activity.programId,
                            mesocycleId: activity.mesocycleId,
                            macrocycleId: activity.macrocycleId,
                            mesoWeekIndex: activity.mesoWeekIndex,
                            weekLabel: activity.weekLabel,
                            intensityModifier: activity.intensityModifier,
                            volumeModifier: activity.volumeModifier,
                            rir: activity.rir,
                          }
                        }
                      });
                    };
                    
                    const justCompleted = activity.activityId === justCompletedActivityId;
                    
                    return (
                      <div 
                        key={activity.activityId} 
                        className={`c-ripple dashboard-activity-card ${isFromProgram ? 'program' : ''} ${statusClass} ${isStrengthWorkout ? 'clickable' : ''} ${justCompleted ? 'just-completed' : ''}`}
                        onClick={handleActivityClick}
                        style={{}}
                      >
                        <div className="dashboard-activity-content">
                          <div className="dashboard-activity-name">{getCategoricalActivityName(activity)}</div>
                          <div className="dashboard-activity-meta">
                            {/* Show time of day if present */}
                            {activity.scheduledTime && (
                              <span className="dashboard-activity-time-of-day">
                                {convertTo12Hour(activity.scheduledTime)}
                              </span>
                            )}
                            {/* Show duration if present */}
                            {(activity.estimatedDuration || activity.duration) && (
                              <>
                                {activity.scheduledTime && <span className="dashboard-activity-meta-separator">·</span>}
                                <span className="dashboard-activity-duration">
                                  {activity.estimatedDuration || activity.duration} min
                                </span>
                              </>
                            )}
                            {/* Progression: week label from mesocycle week_structure (e.g. Deload week) */}
                            {activity.weekLabel && (
                              <>
                                {(activity.scheduledTime || activity.estimatedDuration || activity.duration) && (
                                  <span className="dashboard-activity-meta-separator">·</span>
                                )}
                                <span className="dashboard-activity-week-label">{activity.weekLabel}</span>
                              </>
                            )}
                            {/* Show distance if present */}
                            {activity.estimatedDistance && (
                              <>
                                {(activity.scheduledTime || activity.estimatedDuration || activity.duration) && (
                                  <span className="dashboard-activity-meta-separator">·</span>
                                )}
                                <span className="dashboard-activity-distance">
                                  {activity.estimatedDistance} {activity.distanceUnit || (userSettings.units === 'metric' ? 'km' : 'miles')}
                                </span>
                              </>
                            )}
                          </div>
                          {activity.description && (
                            <div className="dashboard-activity-detail">{activity.description}</div>
                          )}
                        </div>
                        {/* Edit button - for all activities */}
                        <button 
                          className="c-ripple dashboard-activity-edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingActivity(activity);
                            setIsAddActivitiesModalOpen(true);
                          }}
                          title="Edit activity details"
                        >
                          <img src={EditIcon} alt="Edit" />
                        </button>
                        {/* Time edit button - for strength workouts only */}
                        {isStrengthWorkout && (
                          <button 
                            className="c-ripple dashboard-activity-time-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTimeEditActivity(activity);
                              setIsTimeEditModalOpen(true);
                            }}
                            title="Edit scheduled time"
                          >
                            <img src={ClockIcon} alt="Edit time" />
                          </button>
                        )}
                        <button 
                          className={`c-ripple dashboard-activity-complete-btn ${isCompleted ? 'completed' : ''}`}
                          onClick={handleMarkComplete}
                          title={isCompleted ? 'Click to mark incomplete' : 'Click to mark complete'}
                        >
                          <img 
                            src={isCompleted ? CheckCircleIcon : PlaceholderIcon} 
                            alt={isCompleted ? 'Completed' : 'Mark complete'} 
                          />
                        </button>
                      </div>
                    );
                  })
                )}
                {activitiesLoaded && (
                  <div 
                    className="dashboard-activity-card add-activity dashboard-content-loaded"
                    onClick={() => setIsAddActivitiesModalOpen(true)}
                  >
                    <div className="dashboard-activity-content">
                      <span className="add-activity-plus">+</span>
                      <span className="add-activity-text">Add Activity</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Macrocycle, Mesocycle, and Calendar */}
          <div className="dashboard-right-sidebar">
            {/* Macrocycle Card */}
            <div className="dashboard-card dashboard-macrocycle-card">
              <div className="dashboard-macrocycle-content">
                <div className="dashboard-macrocycle-text-section">
                  <h2 className="dashboard-macrocycle-title">Macrocycle</h2>
                  <p className="dashboard-macrocycle-subtitle">{macrocycleName}</p>
                </div>
                <div className="dashboard-macrocycle-days-container">
                  <div className="dashboard-macrocycle-days">
                    {macrocycleDays.length > 0 ? (
                      macrocycleDays.map((day, index) => (
                        <React.Fragment key={index}>
                          {day.isWeekStart && (
                            <div className="dashboard-macrocycle-week-separator" />
                          )}
                          <div 
                            className={`dashboard-macrocycle-day ${day.status}`}
                            title={day.date.toLocaleDateString()}
                          >
                            <div className="dashboard-macrocycle-day-fill" />
                          </div>
                        </React.Fragment>
                      ))
                    ) : (
                      <div className="dashboard-macrocycle-day empty">
                        <div className="dashboard-macrocycle-day-fill" />
                      </div>
                    )}
                  </div>
                  {macrocycleTimeRemaining !== 'N/A' && (
                    <div className="dashboard-macrocycle-time-label">{macrocycleTimeRemaining}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Current Mesocycle Card */}
            <div className="dashboard-card dashboard-mesocycle-card">
              <div className="dashboard-mesocycle-header">
                <h2 className="dashboard-mesocycle-title">Current Mesocycle</h2>
                <span className="dashboard-mesocycle-percent">{Math.round(mesocycleProgress)}% progress</span>
              </div>
              <div className="dashboard-progress-bar-container">
                <div className="dashboard-progress-bar">
                  <div 
                    className="dashboard-progress-fill" 
                    style={{ 
                      width: `${Math.max(0, Math.min(100, mesocycleProgress))}%`,
                      minWidth: mesocycleProgress > 0 ? '2px' : '0px' // Ensure visibility for small values
                    }}
                    title={`Time progress: ${Math.round(mesocycleProgress)}%`}
                  />
                </div>
              </div>
              <div className="dashboard-progress-labels">
                <span className="dashboard-week-label">Week 1</span>
                <span className="dashboard-week-label">Week {mesocycleWeeks}</span>
              </div>
            </div>
          </div>

          {/* Bottom Section - Monthly Activity + Calendar */}
          <div className="dashboard-bottom-section">
            <div className="dashboard-card dashboard-monthly-activity-card">
              <div className="dashboard-card-header">
                <h2>Performance Tracker</h2>
                <p className="dashboard-activity-count">Last 30 days</p>
              </div>
              <div className="dashboard-metrics">
            {/* Skeleton loading for metrics */}
            {!metricsLoaded && (
              <>
                <div className="dashboard-metric">
                  <div className="dashboard-skeleton-chart" />
                  <div className="dashboard-metric-info">
                    <div className="dashboard-skeleton" style={{ height: '16px', width: '60px', marginBottom: '8px' }} />
                    <div className="dashboard-skeleton" style={{ height: '28px', width: '80px', marginBottom: '12px' }} />
                    <div className="dashboard-skeleton" style={{ height: '40px', width: '100%' }} />
                  </div>
                </div>
                <div className="dashboard-metric">
                  <div className="dashboard-skeleton-chart" />
                  <div className="dashboard-metric-info">
                    <div className="dashboard-skeleton" style={{ height: '16px', width: '60px', marginBottom: '8px' }} />
                    <div className="dashboard-skeleton" style={{ height: '28px', width: '80px', marginBottom: '12px' }} />
                    <div className="dashboard-skeleton" style={{ height: '40px', width: '100%' }} />
                  </div>
                </div>
                <div className="dashboard-metric">
                  <div className="dashboard-skeleton-chart" />
                  <div className="dashboard-metric-info">
                    <div className="dashboard-skeleton" style={{ height: '16px', width: '60px', marginBottom: '8px' }} />
                    <div className="dashboard-skeleton" style={{ height: '28px', width: '80px', marginBottom: '12px' }} />
                    <div className="dashboard-skeleton" style={{ height: '40px', width: '100%' }} />
                  </div>
                </div>
              </>
            )}
            
            {/* Loaded metrics */}
            {metricsLoaded && (
            <>
            {/* Lbs Moved Chart */}
            <div className="dashboard-metric dashboard-content-loaded">
              <div className="dashboard-metric-chart">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={monthlyDistanceData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <defs>
                      <linearGradient id={`purpleGradient-${isDark ? 'dark' : 'light'}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(162, 150, 255, 0.40)" />
                        <stop offset="100%" stopColor={isDark ? "rgba(38, 39, 35, 0)" : "rgba(255, 255, 255, 1)"} />
                      </linearGradient>
                      <filter id="purpleShadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#A296FF" floodOpacity={isDark ? "0.4" : "0.2"}/>
                      </filter>
                    </defs>
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#A296FF" 
                      strokeWidth={2}
                      fill={`url(#purpleGradient-${isDark ? 'dark' : 'light'})`}
                      dot={{ fill: 'white', stroke: '#A296FF', strokeWidth: 1, r: 4 }}
                      style={{ filter: 'url(#purpleShadow)' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: isDark ? 'rgba(13, 13, 13, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid rgba(162, 150, 255, 0.3)', 
                        borderRadius: '8px',
                        color: isDark ? '#fff' : '#1A1A1A',
                        fontSize: '12px',
                        padding: '8px 12px'
                      }}
                      labelStyle={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}
                      formatter={(value) => [`${formatDistance(value)}`, 'Distance']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.date || ''}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="dashboard-metric-info">
                <div className="dashboard-metric-label">Distance</div>
                <div className="dashboard-metric-value">{formatDistance(getTotalDistance())}</div>
                <div className="dashboard-metric-stats">
                  <div className="dashboard-metric-stat">
                    <span className="stat-label">Goal</span>
                    <span className="stat-value">{formatDistance(getMonthlyDistanceGoal(userSettings.distanceGoalType, userSettings.distanceGoal))}</span>
                  </div>
                  <div className="dashboard-metric-stat">
                    <span className="stat-label">Daily Avg</span>
                    <span className="stat-value">{formatDistance(getDailyAverageDistance())}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Duration Chart */}
            <div className="dashboard-metric dashboard-content-loaded">
              <div className="dashboard-metric-chart">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={monthlyDurationData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <defs>
                      <linearGradient id={`orangeGradient-${isDark ? 'dark' : 'light'}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(255, 0, 0, 0.40)" />
                        <stop offset="100%" stopColor={isDark ? "rgba(38, 39, 35, 0)" : "rgba(255, 255, 255, 1)"} />
                      </linearGradient>
                      <filter id="redShadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#FF0000" floodOpacity={isDark ? "0.4" : "0.2"}/>
                      </filter>
                    </defs>
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#FF0000" 
                      strokeWidth={2}
                      fill={`url(#orangeGradient-${isDark ? 'dark' : 'light'})`}
                      dot={{ fill: 'white', stroke: '#FF0000', strokeWidth: 1, r: 4 }}
                      style={{ filter: 'url(#redShadow)' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: isDark ? 'rgba(13, 13, 13, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid rgba(255, 0, 0, 0.3)', 
                        borderRadius: '8px',
                        color: isDark ? '#fff' : '#1A1A1A',
                        fontSize: '12px',
                        padding: '8px 12px'
                      }}
                      labelStyle={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}
                      formatter={(value) => [`${formatDuration(value)}`, 'Duration']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.date || ''}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="dashboard-metric-info">
                <div className="dashboard-metric-label">Duration</div>
                <div className="dashboard-metric-value">{formatDuration(getTotalDuration())}</div>
                <div className="dashboard-metric-stats">
                  <div className="dashboard-metric-stat">
                    <span className="stat-label">Goal</span>
                    <span className="stat-value">{formatDuration(getMonthlyDurationGoal(userSettings.durationGoalType, userSettings.durationGoal))}</span>
                  </div>
                  <div className="dashboard-metric-stat">
                    <span className="stat-label">Daily Avg</span>
                    <span className="stat-value">{formatDuration(getDailyAverageDuration())}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Streak */}
            <div className="dashboard-metric dashboard-content-loaded">
              <div className="dashboard-metric-chart dashboard-streak-chart">
                <div className="dashboard-heat-strip">
                  {activityHeatData.map((day, index) => (
                    <div 
                      key={day.date}
                      className={`dashboard-heat-day ${day.hasActivity ? 'active' : ''}`}
                      title={day.hasActivity && day.activities?.length > 0 
                        ? day.activities.map(a => `✓ ${a}`).join('\n')
                        : 'No activity'}
                    />
                  ))}
                </div>
              </div>
              <div className="dashboard-metric-info">
                <div className="dashboard-metric-label">Streak</div>
                <div className="dashboard-metric-value">{currentStreak} days</div>
                <div className="dashboard-metric-stats">
                  <div className="dashboard-metric-stat">
                    <span className="stat-label">Current Streak</span>
                    <span className="stat-value">{currentStreak} days</span>
                  </div>
                  <div className="dashboard-metric-stat">
                    <span className="stat-label">Longest Streak</span>
                    <span className="stat-value">
                      {monthlyStreakData.length > 0 
                        ? Math.max(...monthlyStreakData.map(d => d.value), currentStreak)
                        : currentStreak} days
                    </span>
                  </div>
                </div>
              </div>
            </div>
            </>
            )}
              </div>
            </div>

            {/* Calendar */}
            <div className="dashboard-calendar-card">
              <div className="dashboard-calendar-header">
                <h3 className="dashboard-calendar-title">{calendarMonth}</h3>
                <div className="dashboard-calendar-nav">
                  <button className="c-ripple dashboard-calendar-nav-button" onClick={goToPreviousMonth}>
                    <img src={ChevronLeftDoubleIcon} alt="Previous month" />
                  </button>
                  <button className="c-ripple dashboard-calendar-nav-button" onClick={goToNextMonth}>
                    <img src={ChevronRightDoubleIcon} alt="Next month" />
                  </button>
                </div>
              </div>
              <div className="dashboard-calendar-grid">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className="dashboard-calendar-weekday">{day}</div>
                ))}
                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={`empty-${index}`} className="dashboard-calendar-day empty" />;
                  }
                  const today = new Date();
                  const isToday = day === today.getDate() && 
                                  calendarDate.getMonth() === today.getMonth() &&
                                  calendarDate.getFullYear() === today.getFullYear();
                  // Check if this day has a completed activity (but don't show completed style for today)
                  const hasCompletedActivity = completedDays.includes(day) && !isToday;
                  return (
                    <div 
                      key={day}
                      className={`dashboard-calendar-day ${isToday ? 'today' : ''} ${hasCompletedActivity ? 'completed' : ''}`}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Add Activity Modal */}
      <QuickAddActivityModal
        isOpen={isAddActivitiesModalOpen}
        onClose={() => {
          setIsAddActivitiesModalOpen(false);
          setEditingActivity(null);
        }}
        onSave={async (activityData) => {
          console.log('[Dashboard] Adding activity:', activityData);
          
          try {
            // Use weekDates for consistent date - avoids stale closure issues
            const dateStr = formatLocalDate(weekDates[selectedDayIndex]);
            console.log('[Dashboard] Adding activity for date:', dateStr);
            
            // Add the single activity
            await addSingleActivity(userId, dateStr, activityData);
            
            // Refresh activities for selected day
            const activities = await getScheduledActivitiesForDate(userId, dateStr);
            console.log('[Dashboard] After add, loaded activities for', dateStr, ':', activities.length, 'activities');
            setSelectedDayActivities(activities);
            
            // Refresh completed days for calendar
            const now = new Date();
            const completedDaysList = await getCompletedActivityDays(userId, now.getFullYear(), now.getMonth());
            setCompletedDays(completedDaysList);
          } catch (error) {
            console.error('[Dashboard] Error adding activity:', error);
            alert('Failed to add activity. Please try again.');
          }
        }}
        onEdit={async (activityId, updates) => {
          console.log('[Dashboard] Editing activity:', activityId, updates);
          
          try {
            // Update the activity
            await updateActivityDetails(userId, activityId, updates);
            
            // Refresh activities for selected day
            const dateStr = formatLocalDate(weekDates[selectedDayIndex]);
            const activities = await getScheduledActivitiesForDate(userId, dateStr);
            console.log('[Dashboard] After edit, loaded activities for', dateStr, ':', activities.length, 'activities');
            setSelectedDayActivities(activities);
            
            // Refresh metrics (duration/distance may have changed)
            const now = new Date();
            const metricsData = await getMonthlyActivityMetrics(userId, now.getFullYear(), now.getMonth());
            if (metricsData) {
              setMonthlyDurationData(metricsData.duration || []);
              setMonthlyDistanceData(metricsData.distance || []);
            }
          } catch (error) {
            console.error('[Dashboard] Error editing activity:', error);
            alert('Failed to update activity. Please try again.');
          }
        }}
        onDelete={async (activityId) => {
          console.log('[Dashboard] Deleting activity:', activityId);
          
          try {
            // Delete the activity
            await deleteActivity(userId, activityId);
            
            // Refresh activities for selected day
            const dateStr = formatLocalDate(weekDates[selectedDayIndex]);
            const activities = await getScheduledActivitiesForDate(userId, dateStr);
            console.log('[Dashboard] After delete, loaded activities for', dateStr, ':', activities.length, 'activities');
            setSelectedDayActivities(activities);
            
            // Refresh completed days for calendar (in case we deleted the only activity for that day)
            const now = new Date();
            const completedDaysList = await getCompletedActivityDays(userId, now.getFullYear(), now.getMonth());
            setCompletedDays(completedDaysList);
            
            // Refresh metrics
            const metricsData = await getMonthlyActivityMetrics(userId, now.getFullYear(), now.getMonth());
            if (metricsData) {
              setMonthlyDurationData(metricsData.duration || []);
              setMonthlyDistanceData(metricsData.distance || []);
            }
          } catch (error) {
            console.error('[Dashboard] Error deleting activity:', error);
            alert('Failed to delete activity. Please try again.');
          }
        }}
        date={weekDates[selectedDayIndex]}
        distanceUnit={userSettings.units === 'metric' ? 'km' : 'miles'}
        editActivity={editingActivity}
      />

      {/* Time Edit Modal - for strength workouts */}
      <TimeEditModal
        isOpen={isTimeEditModalOpen}
        onClose={() => {
          setIsTimeEditModalOpen(false);
          setTimeEditActivity(null);
        }}
        activity={timeEditActivity}
        activityName={timeEditActivity ? getCategoricalActivityName(timeEditActivity) : 'Strength Workout'}
        onSaveOne={async (newTime) => {
          console.log('[Dashboard] Updating time for single activity:', timeEditActivity?.activityId, newTime);
          
          try {
            await updateActivityDetails(userId, timeEditActivity.activityId, { scheduledTime: newTime });
            
            // Refresh activities for selected day
            const dateStr = formatLocalDate(weekDates[selectedDayIndex]);
            const activities = await getScheduledActivitiesForDate(userId, dateStr);
            setSelectedDayActivities(activities);
          } catch (error) {
            console.error('[Dashboard] Error updating activity time:', error);
            alert('Failed to update time. Please try again.');
          }
        }}
        onSaveAll={async (newTime) => {
          console.log('[Dashboard] Updating time for all strength workouts:', newTime);
          
          try {
            const result = await updateAllStrengthWorkoutTimes(userId, newTime);
            console.log('[Dashboard] Updated', result.updatedCount, 'strength workouts');
            
            // Refresh activities for selected day
            const dateStr = formatLocalDate(weekDates[selectedDayIndex]);
            const activities = await getScheduledActivitiesForDate(userId, dateStr);
            setSelectedDayActivities(activities);
          } catch (error) {
            console.error('[Dashboard] Error updating all strength workout times:', error);
            alert('Failed to update times. Please try again.');
          }
        }}
      />

      {/* Completed workout finish card – same as Workout page save */}
      {completedWorkoutSession && (
        <WorkoutFinishCard
          summary={{
            duration: (completedWorkoutSession.actualDuration ?? 0) * 60,
            completedSets: completedWorkoutSession.summary?.totalSetsCompleted ?? 0,
            totalReps: completedWorkoutSession.summary?.totalReps ?? 0,
            totalWeight: completedWorkoutSession.summary?.totalLbsMoved ?? 0,
          }}
          onDone={() => setCompletedWorkoutSession(null)}
          showCloseButton
        />
      )}
    </div>

    {/* Footer */}
    <DashboardFooter />

    {/* App Store Download Banner (mobile only) */}
    <AppDownloadBanner />

    {/* Bottom Navigation - Mobile/Tablet */}
    <BottomNav userId={userId} user={user} />
    </>
  );
}

export default Dashboard;

